import gsap from 'gsap';
import { SplitText } from 'gsap/SplitText';

import DetectController from '../../../assets/js/controllers/detect';

import App from '../../../assets/js/app';

import { getElementIndex, parseHydration } from '../../../assets/js/helpers';

gsap.registerPlugin(SplitText);

export default class HeroCaseStudy {

	static selector = 'hero-case-study';

	isAdvocacyProject = false;
	isNextCaseStudy = false;
	isAssetsCreated = false;
	isOpen = false;

	active = 0;

	interval;

	el = {};

	constructor(container) {
		this.el = {
			container,
			heading: container.querySelector('.h1'),
			eyebrow: container.querySelector(`.${this.constructor.selector}__eyebrow > span`),
			body: container.querySelector(`.${this.constructor.selector}__body`),
			index: container.querySelectorAll(`.${this.constructor.selector}__index`),
			instagram: container.querySelector(`.${this.constructor.selector}__instagram`),
			stories: container.querySelector(`.${this.constructor.selector}__instagram-stories`),
			assets: container.querySelector(`.${this.constructor.selector}__instagram-assets`),
			overlay: container.querySelector(`.${this.constructor.selector}__instagram-overlay`),
			prev: container.querySelector(`.${this.constructor.selector}__instagram-prev`),
			next: container.querySelector(`.${this.constructor.selector}__instagram-next`),
			close: container.querySelector(`.${this.constructor.selector}__instagram-close`),
			progress: container.querySelector(`.${this.constructor.selector}__instagram-progress`)
		};

		this.isDesktop = DetectController.isDesktop;

		this.slug = this.el.container.dataset.caseStudySlug;
		this.data = parseHydration(container);
		this.isAdvocacyProject = this.data.categoriesProjects[0].slug === 'advocacy-initiative';

		if (this.isDesktop) {
			this.heading = new SplitText(this.el.heading, {
				type: 'lines'
			});
		}

		this.assets = [];

		this.setIndex();
		this.addEventListeners();
	}

	setIndex() {
		const match = document.querySelector(`.projects__placeholders li[data-case-study-slug="${this.slug}"]`);
		const index = getElementIndex(match);
		for (const indexEl of this.el.index) {
			indexEl.innerHTML = `${(index + 1).toString().padStart(3, '0')}`;
		}
	}

	addEventListeners() {
		if (!this.isAdvocacyProject) {
			this.el.instagram.addEventListener('click', this.#onInstagramClick);
		}

		if (this.isDesktop) {
			App.on('preloadComplete', this.#onPreloadComplete);
			App.on('pageTransition', this.#onPageTransition);
			App.on('pageTransitionNext', this.#onpageTransitionNext);
		}
	}

	removeEventListeners() {
		if (!this.isAdvocacyProject) {
			this.el.instagram.removeEventListener('click', this.#onInstagramClick);
		}

		if (this.isDesktop) {
			App.off('preloadComplete', this.#onPreloadComplete);
			App.off('pageTransition', this.#onPageTransition);
			App.off('pageTransitionNext', this.#onpageTransitionNext);
		}
	}

	addOverlayEventListeners() {
		this.el.overlay.addEventListener('click', this.#onInstagramClose);
		this.el.next.addEventListener('click', this.#onInstagramNext);
		this.el.prev.addEventListener('click', this.#onInstagramPrev);
		this.el.close.addEventListener('click', this.#onInstagramClose);
	}

	removeOverlayEventListeners() {
		this.el.overlay.removeEventListener('click', this.#onInstagramClose);
		this.el.next.removeEventListener('click', this.#onInstagramNext);
		this.el.prev.removeEventListener('click', this.#onInstagramPrev);
		this.el.close.addEventListener('click', this.#onInstagramClose);
	}

	#onPreloadComplete = () => {
		this.isDelay = true;
		this.show();
	};

	#onPageTransition = () => {
		this.show();
	};

	#onpageTransitionNext = () => {
		this.isDelay = true;
		this.isNextCaseStudy = true;
		this.show();
	};

	show() {
		this.timelineShowHeading = null;
		this.timelineShowHeading = gsap.timeline({
			delay: this.isDelay ? 0.25 : 0,
			onComplete: () => {
				this.timelineShowHeading.kill();
			}
		});

		if (!this.isNextCaseStudy) {
			const rotations = [40, 50, 60, 70, 80];

			for (const [i, line] of this.heading.lines.entries()) {
				gsap.set(line, {
					autoAlpha: 0,
					scale: 1 - i * 0.05,
					rotationX: rotations[i] * -1,
					y: this.heading.lines[0].offsetHeight,
					transformOrigin: '50% 50% -150'
				});
			}

			for (const [i, line] of this.heading.lines.entries()) {
				this.timelineShowHeading
					.to(line, {
						ease: 'power4.inOut',
						rotationX: 0,
						y: 0,
						scale: 1,
						autoAlpha: 1,
						duration: 1.5,
						delay: i === 0 ? 0 : i * 0.1,
						transformOrigin: '50% 50% -150'
					}, 'start');
			}
		}

		this.timelineShowHeading
			.fromTo(this.el.eyebrow, {
				// y: '100%',
				autoAlpha: 0
			}, {
				ease: 'power2.out',
				autoAlpha: 1,
				// y: '0%',
				duration: 0.5
			}, `start+=${!this.isNextCaseStudy ? 1 : 0}`)
			.fromTo(this.el.body, {
				autoAlpha: 0,
				y: 30
			}, {
				ease: 'expo.out',
				y: 0,
				autoAlpha: 1,
				duration: 1.25
			}, `start+=${!this.isNextCaseStudy ? 1.25 : 0}`);

			if (!this.isAdvocacyProject) {
				this.timelineShowHeading
					.fromTo(this.el.instagram, {
						autoAlpha: 0
					}, {
						ease: 'power2.out',
						autoAlpha: 1,
						duration: 0.5
					}, `start+=${!this.isNextCaseStudy ? 1.25 : 0}`);
			}
	}

	#onInstagramNext = (e) => {
		const { currentTarget } = e;
		currentTarget.disabled = true;

		setTimeout(() => {
			currentTarget.disabled = false;
		}, 175);

		this.nextSlide();
		this.autoplay();
	}

	#onInstagramPrev = (e) => {
		const { currentTarget } = e;
		currentTarget.disabled = true;

		setTimeout(() => {
			currentTarget.disabled = false;
		}, 175);

		this.prevSlide();
		this.autoplay();
	}

	#onInstagramClick = () => {
		App.freeze();

		if (!this.isOpen) {
			this.isOpen = true;
			this.addOverlayEventListeners();
			this.timelineShow = gsap.timeline();
			this.timelineShow
				.set(this.el.stories, {
					display: 'block'
				})
				.fromTo(this.el.stories, {
					autoAlpha: 0
				}, {
					autoAlpha: 1,
					duration: 0.25,
					ease: 'power4.easeOut'
				});
		}

		if (!this.isAssetsCreated) {
			this.isAssetsCreated = true;
			this.createAssets();
		} else {
			this.activateSlide();
			this.autoplay();
		}

	}

	#onInstagramClose = () => {
		App.thaw();

		this.removeOverlayEventListeners();

		this.timelineHide = gsap.timeline({
			onComplete: () => {
				this.isOpen = false;
				this.deactivateSlide();
				if (this.interval) clearInterval(this.interval);
			}
		});
		this.timelineHide
			.fromTo(this.el.stories, {
				autoAlpha: 1
			}, {
				autoAlpha: 0,
				duration: 0.125,
				ease: 'power4.easeOut'
			})
			.set(this.el.stories, {
				display: 'none',
				delay: 0.125
			});
	}

	autoplay() {
		const delay = this.assets[this.active].kind === 'video' ? this.assets[this.active].el.duration * 1000 : 6000;
		if (this.interval) clearInterval(this.interval);
		this.interval = setInterval(() => {
			this.nextSlide();
		}, delay);
	}

	prevSlide() {
		this.deactivateSlide();

		if (this.active > 0) {
			this.active -= 1;
		} else {
			this.active = this.assets.length - 1;
		}

		this.activateSlide();
	}

	nextSlide() {
		this.deactivateSlide();

		if (this.active < this.assets.length - 1) {
			this.active += 1;
		} else {
			this.active = 0;
		}

		this.activateSlide();
	}

	deactivateSlide() {
		if (this.assets[this.active]) {
			this.assets[this.active].wrap.classList.remove('is-active');
			this.assets[this.active].progressBar.classList.remove('is-active');

			if (this.assets[this.active].kind === 'video') {
				this.assets[this.active].el.pause();
				this.assets[this.active].el.currentTime = 0;
			}
		}
	}

	activateSlide() {
		for (let i = 0; i < this.assets.length; i++) {
			const item = this.assets[i];
			if (i < this.active) {
				item.progressBar.classList.add('was-active');
			} else {
				item.progressBar.classList.remove('was-active');
			}
		}
		this.assets[this.active].wrap.classList.add('is-active');
		this.assets[this.active].progressBar.classList.add('is-active');

		if (this.assets[this.active].kind === 'video') {
			this.assets[this.active].el.play();
		}
	}

	createAssets() {
		for (const [i, data] of this.data.instagramStories.entries()) {
			let asset;

			if (data.kind === 'video') {
				asset = document.createElement('video');
			} else {
				asset = document.createElement('img');
			}

			const wrap = document.createElement('div');
			const progress = document.createElement('div');
			const progressBar = document.createElement('span');

			if (data.kind === 'video') {
				asset.onloadedmetadata = () => {
					const { duration } = asset;
					progressBar.style.animationDuration = `${duration * 1000}ms`;
				};

				asset.oncanplay = (e) => {
					if (e.currentTarget.parentNode.classList.contains('is-active')) {
						this.active = parseInt(e.currentTarget.parentNode.getAttribute('data-slide-index'));
						this.activateSlide();
						this.autoplay();
					} else {
						asset.pause();
					}
				};
			} else {
				progressBar.style.animationDuration = '6000ms';
				asset.onload = (e) => {
					if (e.currentTarget.parentNode.classList.contains('is-active')) {
						e.currentTarget.classList.add('is-loaded');
						this.active = parseInt(e.currentTarget.parentNode.getAttribute('data-slide-index'));
						this.activateSlide();
						this.autoplay();
					}
				};
			}


			wrap.setAttribute('data-slide-index', i);
			wrap.classList.add('hero-case-study__instagram-asset-slide');
			if (i === 0) wrap.classList.add('is-active');

			progress.classList.add('hero-case-study__instagram-progress-bar-wrap');
			progressBar.classList.add('hero-case-study__instagram-progress-bar');

			asset.setAttribute('alt', data.alt);

			if (data.kind === 'video') {
				asset.setAttribute('playsinline', true);
				asset.setAttribute('preload', 'metadata');
			}

			asset.src = data.url;

			if (data.kind === 'video') {
				asset.playsinline = true;
				asset.preload = 'metadata';
			}

			wrap.appendChild(asset);
			progress.appendChild(progressBar);

			this.assets.push({
				wrap,
				progressBar,
				el: asset,
				kind: data.kind
			});

			this.el.assets.appendChild(wrap);
			this.el.progress.appendChild(progress);

			if (data.kind === 'video') asset.load();
		}
	}

	destroy() {
		if (this.interval) clearInterval(this.interval);
		if (!this.isAdvocacyProject) this.removeOverlayEventListeners();
		this.removeEventListeners();
	}

}
